import AuthLayout from '@/components/AuthLayout';
import AppButton from '@/components/common/AppButton/AppButton';
import AppFormField from '@/components/common/AppFormField';
import { yupResolver } from '@hookform/resolvers/yup';
import { AlertTriangle, ArrowLeft, CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { sendPasswordResetEmail } from '../api/users';

const RESEND_COOLDOWN_SECONDS = 60;

const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email'),
});

type ForgotPasswordFormValues = yup.InferType<typeof forgotPasswordSchema>;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const startCooldown = () => {
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const methods = useForm<ForgotPasswordFormValues>({
    defaultValues: { email: '' },
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setLoading(true);
    setSubmitError('');

    try {
      const result = await sendPasswordResetEmail({ email: data.email });

      if (result.error) {
        setSubmitError(result.error);
        return;
      }

      toast.success('Password reset email sent!');
      setSubmittedEmail(data.email);
      setSuccess(true);
      startCooldown();
    } catch {
      setSubmitError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!submittedEmail || cooldownSeconds > 0) {
      return;
    }

    setResending(true);
    try {
      const result = await sendPasswordResetEmail({ email: submittedEmail });

      if (result.error) {
        toast.error(result.error);
        return;
      }

      toast.success('Password reset email sent!');
      startCooldown();
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <AuthLayout title="Check your email">
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="flex flex-col gap-4">
            <p>We've sent an email with a password reset link to:</p>
            <p className="font-semibold text-center">{submittedEmail}</p>
            <p className="text-light text-sm">
              Note: If you don't see the email in your inbox, check your spam
              folder. If it's not there, the email address may not be confirmed,
              or it may not match an existing Pool Guy account.
            </p>
          </div>

          <div className="px-8 flex flex-col gap-6 md:gap-8">
            <AppButton
              fullWidth
              variant="secondary"
              loading={resending}
              disabled={resending || cooldownSeconds > 0}
              onClick={handleResendEmail}
            >
              {cooldownSeconds > 0
                ? `RE-SEND IN ${cooldownSeconds}s`
                : 'RE-SEND EMAIL'}
            </AppButton>

            <AppButton
              fullWidth
              variant="tertiary"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft />
              BACK TO LOGIN
            </AppButton>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 md:gap-8"
        >
          <div className="flex flex-col gap-4">
            {submitError && (
              <div className="flex items-center gap-2 bg-danger/20 text-danger p-2 rounded-lg">
                <CircleAlert />
                {submitError}
              </div>
            )}

            <AppFormField<ForgotPasswordFormValues>
              name="email"
              label="Email Address"
              type="email"
              placeholder="Enter your email"
            />

            <p>
              We'll send a password reset link to this email if it matches an
              existing Pool Guy account.
            </p>
          </div>

          <div className="px-8 flex flex-col gap-6 md:gap-8">
            <AppButton type="submit" fullWidth loading={loading}>
              Send Reset Email
            </AppButton>

            <AppButton
              type="button"
              fullWidth
              variant="tertiary"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft />
              BACK TO LOGIN
            </AppButton>
          </div>

          <div className="bg-warning/20 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className=" text-warning flex-shrink-0" />
            <p>
              Having issues resetting your password? Please contact us at{' '}
              <a
                href="mailto:support@getpoolguy.com"
                className="text-primary underline"
              >
                support@getpoolguy.com
              </a>
            </p>
          </div>
        </form>
      </FormProvider>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
